import React, {
    useEffect,
    useRef,
    useState
} from "react";

import {
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    Button,
    CircularProgress,
    Alert
} from "@mui/material";
 import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    getWLSCurrentMonth,
    getWLS,
    saveWLS
} from "../../../services/wlsService";
import PageHeader from "../../../utils/PageHeader";


// ============================================================
// MONTH NAMES
// ============================================================

const monthNames = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];


// ============================================================
// COMPONENT
// ============================================================

const WLSDataEntry = () => {

    // ========================================================
    // STATE
    // ========================================================

    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");

    const [wlsDr, setWlsDr] = useState("0");
    const [wlsCr, setWlsCr] = useState("0");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");


    // ========================================================
    // REFS
    // ========================================================

    const debitRef = useRef(null);
    const creditRef = useRef(null);
    const saveRef = useRef(null);


    // ========================================================
    // MONTH NAME
    // ========================================================

    const monthName =
        month
            ? monthNames[Number(month)]
            : "";


    // ========================================================
    // LOAD CURRENT MONTH
    // ========================================================

    const loadCurrentMonth = async () => {

        try {

            setLoading(true);

            setMessage("");
            setMessageType("");

            const res =
                await getWLSCurrentMonth();

            const data =
                res.data.data;

            const currentMonth =
                Number(data.month);

            const currentYear =
                Number(data.year);


            setMonth(currentMonth);
            setYear(currentYear);


            // IMPORTANT:
            // Use the returned values directly.
            // Do not use month/year state immediately
            // after setMonth/setYear.

            await loadWLS(
                currentMonth,
                currentYear
            );

        } catch (err) {

            console.error(
                "Current month error:",
                err
            );

            setMessage(
                err.response?.data?.message ||
                "Unable to load current month"
            );

            setMessageType("error");

        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // LOAD WLS DATA
    // ========================================================

    const loadWLS = async (
        selectedMonth,
        selectedYear
    ) => {

        try {

            const res =
                await getWLS(
                    selectedMonth,
                    selectedYear
                );

            const data =
                res.data.data;


            if (data) {

                setWlsDr(
                    data.wls_dr !== null &&
                    data.wls_dr !== undefined
                        ? String(data.wls_dr)
                        : "0"
                );


                setWlsCr(
                    data.wls_cr !== null &&
                    data.wls_cr !== undefined
                        ? String(data.wls_cr)
                        : "0"
                );

            } else {

                // No entry for this month

                setWlsDr("0");
                setWlsCr("0");

            }

        } catch (err) {

            console.error(
                "WLS load error:",
                err
            );

            setMessage(
                err.response?.data?.message ||
                "Unable to load WLS data"
            );

            setMessageType("error");

        }

    };


    // ========================================================
    // INITIAL LOAD
    // ========================================================

    useEffect(() => {

        loadCurrentMonth();

    }, []);


    // ========================================================
    // SAVE WLS
    // ========================================================

   const handleSave = async () => {

    setMessage("");
    setMessageType("");


    // ----------------------------------------------------
    // GET TEXT VALUES
    // ----------------------------------------------------

    const debitText =
        String(wlsDr).trim();

    const creditText =
        String(wlsCr).trim();


    // ----------------------------------------------------
    // VALIDATE DEBIT
    // ----------------------------------------------------

    if (
        debitText === "" ||
        !/^-?\d+(\.\d+)?$/.test(debitText)
    ) {

        setMessage(
            "Please enter a valid WLS Debit amount"
        );

        setMessageType("error");

        debitRef.current?.focus();

        return;
    }


    // ----------------------------------------------------
    // VALIDATE CREDIT
    // ----------------------------------------------------

    if (
        creditText === "" ||
        !/^-?\d+(\.\d+)?$/.test(creditText)
    ) {

        setMessage(
            "Please enter a valid WLS Credit amount"
        );

        setMessageType("error");

        creditRef.current?.focus();

        return;
    }


    // ----------------------------------------------------
    // CONVERT
    // ----------------------------------------------------

    const debit =
        Number(debitText);

    const credit =
        Number(creditText);


    // ----------------------------------------------------
    // EXTRA SAFETY CHECK
    // ----------------------------------------------------

    if (
        !Number.isFinite(debit)
    ) {

        setMessage(
            "Please enter a valid WLS Debit amount"
        );

        setMessageType("error");

        debitRef.current?.focus();

        return;
    }


    if (
        !Number.isFinite(credit)
    ) {

        setMessage(
            "Please enter a valid WLS Credit amount"
        );

        setMessageType("error");

        creditRef.current?.focus();

        return;
    }


    // ----------------------------------------------------
    // MONTH / YEAR
    // ----------------------------------------------------

    if (!month || !year) {

        setMessage(
            "Month and year not available"
        );

        setMessageType("error");

        return;
    }


    // ----------------------------------------------------
    // SAVE
    // ----------------------------------------------------

    try {

        setSaving(true);

        const payload = {

            wls_dr: debit,

            wls_cr: credit,

            PMONTH: Number(month),

            PYEAR: Number(year)

        };

        const res =
            await saveWLS(payload);


        setMessage(
            res.data.message ||
            "WLS data saved successfully"
        );

        setMessageType("success");


        await loadWLS(
            Number(month),
            Number(year)
        );


        setTimeout(() => {

            debitRef.current?.focus();

            debitRef.current?.select();

        }, 100);


    } catch (err) {

        console.error(
            "WLS save error:",
            err
        );

        setMessage(
            err.response?.data?.message ||
            "Unable to save WLS data"
        );

        setMessageType("error");

    } finally {

        setSaving(false);

    }

};


    // ========================================================
    // ENTER KEY - DEBIT
    // ========================================================

    const handleDebitKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            creditRef.current?.focus();

            creditRef.current?.select();

        }

    };


    // ========================================================
    // ENTER KEY - CREDIT
    // ========================================================

    const handleCreditKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            saveRef.current?.focus();

        }

    };


    // ========================================================
    // ENTER KEY - SAVE
    // ========================================================

    const handleSaveKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            if (!saving) {

                handleSave();

            }

        }

    };


    // ========================================================
    // LOADING SCREEN
    // ========================================================

    if (loading) {

        return (

            <Box
                sx={{
                    width: "100%",
                    minHeight: "200px",

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center"
                }}
            >

                <CircularProgress />

            </Box>

        );

    }


    // ========================================================
    // MAIN UI
    // ========================================================

    return (

        <Box
            sx={{
                width: "100%",

                boxSizing: "border-box",

                p: {
                    xs: 0.5,
                    sm: 1,
                    md: 1.5
                }
            }}
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <PageHeader
        title=" WLS Data Entry"
        subtitle={`Data entry status for the month of: ${monthName} - ${year}`}
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        // statusChip={{
        //   label: loading
        //     ? "Loading..."
        //     : totalRows > 0
        //       ? "Data Found"
        //       : "No Data",
        //   color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        // }}
      //  showRefresh
       // onRefresh={handleRefresh}
        // refreshing={loading}
      />




     


            {/* =================================================
                ENTRY CARD
            ================================================= */}

            <Card
                elevation={2}
                sx={{
                    width: "100%",

                    borderRadius: 2,

                    boxSizing: "border-box"
                }}
            >

                <CardContent
                    sx={{
                        p: {
                            xs: 1.5,
                            sm: 2,
                            md: 2.5
                        },

                        "&:last-child": {
                            pb: {
                                xs: 1.5,
                                sm: 2,
                                md: 2.5
                            }
                        }
                    }}
                >

                    {/* =========================================
                        ENTRY AREA
                    ========================================= */}

                    <Box
                        sx={{
                            width: "100%",

                            display: "flex",

                            flexDirection: {
                                xs: "column",
                                sm: "row"
                            },

                            alignItems: {
                                xs: "stretch",
                                sm: "center"
                            },

                            gap: {
                                xs: 1.5,
                                sm: 2,
                                md: 3
                            },

                            boxSizing: "border-box"
                        }}
                    >

                        {/* =====================================
                            WLS DEBIT
                        ===================================== */}

                        <Box
                            sx={{
                                display: "flex",

                                alignItems: "center",

                                gap: {
                                    xs: 1,
                                    sm: 1.5
                                },

                                flex: 1,

                                minWidth: 0
                            }}
                        >

                            <Typography
                                component="label"
                                htmlFor="wlsDebit"
                                sx={{
                                    color: "#e60023",

                                    fontWeight: 700,

                                    fontSize: {
                                        xs: "0.9rem",
                                        sm: "0.95rem"
                                    },

                                    whiteSpace: "nowrap"
                                }}
                            >

                                WLS Debit

                            </Typography>


                            <TextField
    id="wlsDebit"

    inputRef={debitRef}

    type="text"

    inputMode="decimal"

    value={wlsDr}

    onChange={(e) => {
        setWlsDr(e.target.value);
    }}

    onKeyDown={handleDebitKeyDown}

    autoComplete="off"

    size="small"

    fullWidth

    sx={{
        maxWidth: {
            xs: "100%",
            sm: 160
        },

        "& input": {
            fontSize: {
                xs: "16px",
                sm: "15px"
            }
        }
    }}
/>

                        </Box>


                        {/* =====================================
                            WLS CREDIT
                        ===================================== */}

                        <Box
                            sx={{
                                display: "flex",

                                alignItems: "center",

                                gap: {
                                    xs: 1,
                                    sm: 1.5
                                },

                                flex: 1,

                                minWidth: 0
                            }}
                        >

                            <Typography
                                component="label"
                                htmlFor="wlsCredit"
                                sx={{
                                    color: "#e60023",

                                    fontWeight: 700,

                                    fontSize: {
                                        xs: "0.9rem",
                                        sm: "0.95rem"
                                    },

                                    whiteSpace: "nowrap"
                                }}
                            >

                                WLS Credit

                            </Typography>


                           <TextField
    id="wlsCredit"

    inputRef={creditRef}

    type="text"

    inputMode="decimal"

    value={wlsCr}

    onChange={(e) => {
        setWlsCr(e.target.value);
    }}

    onKeyDown={handleCreditKeyDown}

    autoComplete="off"

    size="small"

    fullWidth

    sx={{
        maxWidth: {
            xs: "100%",
            sm: 160
        },

        "& input": {
            fontSize: {
                xs: "16px",
                sm: "15px"
            }
        }
    }}
/>

                        </Box>


                        {/* =====================================
                            SAVE
                        ===================================== */}

                        <Button
                            ref={saveRef}

                            variant="contained"

                            type="button"

                            onClick={handleSave}

                            onKeyDown={
                                handleSaveKeyDown
                            }

                            disabled={saving}

                            sx={{
                                minWidth: {
                                    xs: "100%",
                                    sm: 110
                                },

                                height: 40,

                                flexShrink: 0,

                                bgcolor: "#ffff00",

                                color: "#000000",

                                border: "1px solid #d6b500",

                                fontWeight: 700,

                                "&:hover": {
                                    bgcolor: "#ffeb00"
                                }
                            }}
                        >

                            {saving ? (
                                <>
                                    <CircularProgress
                                        size={20}
                                        sx={{
                                            mr: 1,
                                            color: "#000"
                                        }}
                                    />

                                    Saving...
                                </>
                            ) : (
                                "Save"
                            )}

                        </Button>

                    </Box>


                    {/* =================================================
                        MESSAGE
                    ================================================= */}

                    {message && (

                        <Alert
                            severity={
                                messageType === "success"
                                    ? "success"
                                    : "error"
                            }

                            onClose={() => {

                                setMessage("");

                                setMessageType("");

                            }}

                            sx={{
                                mt: 2
                            }}
                        >

                            {message}

                        </Alert>

                    )}

                </CardContent>

            </Card>

        </Box>

    );

};


export default WLSDataEntry;